import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, InputNumber, Switch, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import { formatUsdCentsTr } from "./channelKind";
import { SONIC_PROSPECT_PRICING, formatProspectUsdTr } from "util/prospectBilling";
import { listQualifiedProspects, updateSpendProtection } from "./prospectService";
import ConversationsCard from "./ConversationsCard";
import { apiRequest } from "util/services";
import styles from "./style.module.scss";

function limitState(protection) {
  if (!protection) return "none";
  const hasQp = protection.monthly_qp_limit != null;
  const hasSpend = protection.monthly_spend_cap_cents != null;
  if (!hasQp && !hasSpend) return "none";
  const state = protection.state || protection.status;
  if (state === "hard_stop" || state === "exceeded") return "over";
  if (state === "warning") return "near";
  const qpLimit = Number(protection.monthly_qp_limit);
  const qpCount = Number(protection.monthly_qp_count || 0);
  if (Number.isFinite(qpLimit) && qpLimit > 0 && qpCount / qpLimit >= 0.8) return "near";
  const spendCap = Number(protection.monthly_spend_cap_cents);
  const spend = Number(protection.monthly_estimated_spend_cents || 0);
  if (Number.isFinite(spendCap) && spendCap > 0 && spend / spendCap >= 0.8) return "near";
  return "ok";
}

const BillingDrawer = ({ open, onClose, bots }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [protection, setProtection] = useState(null);
  const [limitOn, setLimitOn] = useState(false);
  const [qpLimit, setQpLimit] = useState(null);
  const [spendLimit, setSpendLimit] = useState(null);
  const [card, setCard] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    apiRequest("subscriptions/sonic-prospect/self-serve/status", "POST", {}, true).then(
      (result) => {
        if (result) setCard(result);
      }
    );
    const ids = (bots || []).map((bot) => bot.id).filter(Boolean);
    if (!ids.length) {
      setRows([]);
      setPricing(null);
      setProtection(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      ids.map(async (id) => {
        try {
          const res = await listQualifiedProspects(id, { limit: 1, offset: 0 });
          return { id, res };
        } catch (_) {
          return { id, res: null };
        }
      }),
    ).then((list) => {
      if (cancelled) return;
      const first = list.find((row) => row.res)?.res || null;
      const guard = first?.spendProtection || null;
      setPricing(first?.pricing || null);
      setProtection(guard);
      setRows(
        list.map((row) => ({
          id: row.id,
          name:
            (bots || []).find((bot) => bot.id === row.id)?.name ||
            t("sonic_prospect.preview_bot_fallback"),
          count: row.res?.count || 0,
          orgCount: row.res?.orgCount || 0,
        })),
      );
      const enabled =
        guard?.monthly_qp_limit != null || guard?.monthly_spend_cap_cents != null;
      setLimitOn(Boolean(enabled));
      setQpLimit(guard?.monthly_qp_limit ?? null);
      setSpendLimit(
        guard?.monthly_spend_cap_cents != null
          ? Number(guard.monthly_spend_cap_cents) / 100
          : SONIC_PROSPECT_PRICING.defaultCap,
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, bots, t]);

  const monthCount = protection?.monthly_qp_count ?? rows.reduce((sum, row) => sum + row.count, 0);
  const lifetime = rows.reduce((max, row) => Math.max(max, row.orgCount || 0), 0);
  const freeLimit = SONIC_PROSPECT_PRICING.freeLifetimeQp;
  const freeUsed = Math.min(lifetime, freeLimit);
  const freeDone = lifetime >= freeLimit;
  const launch = formatProspectUsdTr(SONIC_PROSPECT_PRICING.tier1Price);
  const standard = formatProspectUsdTr(SONIC_PROSPECT_PRICING.tier2Price);
  const threshold = SONIC_PROSPECT_PRICING.tier1Limit;
  const capUsd = spendLimit == null || spendLimit === ""
    ? SONIC_PROSPECT_PRICING.defaultCap
    : Number(spendLimit);
  const monthSpend = Number(protection?.monthly_estimated_spend_cents || 0) / 100;
  const capRatio = capUsd > 0 ? monthSpend / capUsd : 0;
  const capBanner = capRatio >= 1 ? "full" : capRatio >= 0.8 ? "high" : capRatio >= 0.5 ? "mid" : "";
  const estimate = formatUsdCentsTr(
    pricing?.estimated_spend_cents ?? protection?.monthly_estimated_spend_cents ?? 0,
  );
  const badge = limitState(protection);
  const badgeColor = { none: "default", ok: "success", near: "warning", over: "error" }[badge];

  const canSave = useMemo(() => {
    if (!limitOn) return true;
    const qpOk = qpLimit == null || Number(qpLimit) >= 1;
    const spendOk = spendLimit == null || Number(spendLimit) > 0;
    return qpOk && spendOk && (qpLimit != null || spendLimit != null);
  }, [limitOn, qpLimit, spendLimit]);

  const save = async () => {
    const ids = (bots || []).map((bot) => bot.id).filter(Boolean);
    if (!ids.length) return;
    setSaving(true);
    try {
      const payload = {
        monthly_qp_limit: limitOn && qpLimit != null ? Number(qpLimit) : null,
        monthly_spend_cap_cents:
          spendLimit == null || spendLimit === ""
            ? Math.round(SONIC_PROSPECT_PRICING.defaultCap * 100)
            : Math.round(Number(spendLimit) * 100),
      };
      let next = null;
      for (const id of ids) {
        next = await updateSpendProtection(id, payload);
      }
      setProtection(next);
      setLimitOn(
        next?.monthly_qp_limit != null || next?.monthly_spend_cap_cents != null,
      );
      message.success(t("sonic_prospect.spend_protection_saved"));
    } catch (err) {
      message.error(err?.message || t("sonic_prospect.spend_protection_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={440}
      title={t("sonic_prospect.header_billing")}
      className={styles.billingDrawer}
    >
      <div className={styles.billingStack}>
        <div className={styles.coverageBlock}>
          <div className={styles.coverageLabel}>
            <span>Card</span>
            <span>
              {card?.card_last4
                ? `${card.card_brand || "Card"} ···· ${card.card_last4}`
                : "No card saved"}
            </span>
          </div>
          <p className={styles.billingNote}>
            {card
              ? `${card.free_remaining ?? 0} free leads remaining · this month ${
                  card.usage_this_month_cents == null
                    ? "—"
                    : `$${(Number(card.usage_this_month_cents) / 100).toFixed(2)}`
                }`
              : ""}
          </p>
          <Button
            onClick={async () => {
              const result = await apiRequest(
                "subscriptions/sonic-prospect/self-serve/portal",
                "POST",
                { return_url: window.location.href },
                true
              );
              if (result?.url) window.location.assign(result.url);
            }}
          >
            Update card
          </Button>
          {(card?.invoices || []).length ? (
            <ul>
              {card.invoices.map((invoice) => (
                <li key={invoice.id}>
                  <a href={invoice.hosted_invoice_url || "#"} target="_blank" rel="noopener noreferrer">
                    {invoice.status} · ${((invoice.total || 0) / 100).toFixed(2)}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.billingNote}>No invoices yet.</p>
          )}
        </div>
        <div className={styles.coverageBlock}>
          <div className={styles.coverageLabel}>
            <span>{t("sonic_prospect.billing_free_label")}</span>
            <span>
              {t("sonic_prospect.billing_free_value", { used: freeUsed, max: freeLimit })}
            </span>
          </div>
          <div className={styles.coverageTrack} aria-hidden="true">
            <span style={{ width: `${freeLimit ? Math.min(100, (freeUsed / freeLimit) * 100) : 0}%` }} />
          </div>
          {freeDone ? (
            <p className={styles.billingNote}>
              {t("sonic_prospect.billing_free_done", { price: launch })}
            </p>
          ) : null}
        </div>
        {capBanner ? (
          <p className={styles.capBanner}>{t(`sonic_prospect.billing_cap_${capBanner}`)}</p>
        ) : null}
        <Tag color={badgeColor}>{t(`sonic_prospect.billing_state_${badge}`)}</Tag>
        <dl className={styles.billingFacts}>
          <div>
            <dt>{t("sonic_prospect.billing_month_qp")}</dt>
            <dd>{loading ? "…" : monthCount}</dd>
          </div>
          <div>
            <dt>{t("sonic_prospect.billing_unit")}</dt>
            <dd>
              {t("sonic_prospect.billing_unit_value", {
                launch,
                count: threshold,
                standard,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("sonic_prospect.billing_estimate")}</dt>
            <dd>{loading ? "…" : estimate}</dd>
          </div>
        </dl>
        <p className={styles.billingNote}>{t("sonic_prospect.billing_note")}</p>

        <label className={styles.billingToggle}>
          <span>{t("sonic_prospect.billing_toggle")}</span>
          <Switch
            checked={limitOn}
            onChange={setLimitOn}
            checkedChildren={t("sonic_prospect.billing_toggle")}
            unCheckedChildren={t("sonic_prospect.billing_unlimited")}
          />
        </label>
        <label className={styles.billingForm}>
          <span>{t("sonic_prospect.billing_spend_limit")}</span>
          <InputNumber
            min={0.01}
            value={spendLimit ?? SONIC_PROSPECT_PRICING.defaultCap}
            onChange={setSpendLimit}
            controls={false}
            style={{ width: "100%" }}
          />
          <span className={styles.billingNote}>{t("sonic_prospect.billing_cap_help")}</span>
        </label>
        {!limitOn ? (
          <p className={styles.billingNote}>{t("sonic_prospect.billing_unlimited")}</p>
        ) : (
          <div className={styles.billingForm}>
            <label>
              <span>{t("sonic_prospect.billing_qp_limit")}</span>
              <InputNumber
                min={1}
                value={qpLimit}
                onChange={setQpLimit}
                style={{ width: "100%" }}
              />
            </label>
            {!canSave ? (
              <p className={styles.billingError}>{t("sonic_prospect.billing_invalid")}</p>
            ) : null}
          </div>
        )}
        <Button type="primary" loading={saving} disabled={!canSave} onClick={save}>
          {t("sonic_prospect.spend_protection_save")}
        </Button>

        {open ? <ConversationsCard /> : null}

        <h3>{t("sonic_prospect.billing_by_bot")}</h3>
        {rows.length === 0 || rows.every((row) => !row.count) ? (
          <p className={styles.billingEmpty}>{t("sonic_prospect.billing_empty")}</p>
        ) : (
          <table className={styles.billingTable}>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Drawer>
  );
};

export default BillingDrawer;
